import type { Job } from "../types/Job";

/** What a pasted job reference turned out to be. */
export type JobReference =
	| { kind: "id"; id: string }
	| { kind: "name"; name: string }
	| { kind: "invalid"; reason: string };

/** An Airtable record id: `rec` and alphanumerics — exactly fourteen of them from Airtable, a
 *  few more from the offline seed, so the floor is what is checked. */
const RECORD_ID = /^rec[A-Za-z0-9]{14,}$/;
/** Any airtable.com link. When it carries a record, that is the path segment starting with
 *  `rec`, as in https://airtable.com/appX/tblY/viwZ/recW?blocks=hide. */
const AIRTABLE_URL = /^https?:\/\/(?:[\w-]+\.)?airtable\.com\//i;
const RECORD_ID_IN_URL = /\/(rec[A-Za-z0-9]{14,})(?:[/?#]|$)/;

/** Reads a pasted job reference: a bare record id, an Airtable link carrying one, or failing
 *  both, the job's name as Airtable shows it. Nothing at all is invalid rather than a name. */
export function parseJobReference(raw: string): JobReference {
	const text = raw.trim();
	if (!text) return { kind: "invalid", reason: "Paste a record id, an Airtable link, or the job's name." };
	if (RECORD_ID.test(text)) return { kind: "id", id: text };
	if (AIRTABLE_URL.test(text)) {
		const match = RECORD_ID_IN_URL.exec(text);
		return match
			? { kind: "id", id: match[1] }
			: {
					kind: "invalid",
					reason: "That Airtable link has no record in it — open the job and copy the link from there.",
				};
	}
	return { kind: "name", name: text };
}

/** The jobs whose name is the pasted one, compared the way Airtable compares text: the whole
 *  string, case-insensitive. Surrounding whitespace is the paste's, not the name's. */
export function jobsNamed<T extends Pick<Job, "name">>(jobs: readonly T[], name: string): T[] {
	const wanted = name.trim().toLowerCase();
	return jobs.filter((job) => (job.name ?? "").trim().toLowerCase() === wanted);
}
